import { Content, Portal, Root, Trigger } from '@radix-ui/react-hover-card'
import { type MouseEvent, type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { type FloatingContentType, type FloatingInteractOutsideType } from '@/defs/floating'

import { FloatingProvider } from '../../FloatingProvider/FloatingProvider'
import { getFloatingContentProps } from '../getFloatingContentProps'

const CLOSE_DELAY = 100
const OPEN_DELAY = 100

type Props = {
  children: ReactNode
  className?: string
  forceClosed?: boolean
  isClickable?: boolean
  trigger: ReactElement
} & Pick<FloatingContentType, 'ignoreScopes' | 'placement'>

export const FloatingHoverRoot = ({
  children,
  className,
  forceClosed,
  ignoreScopes,
  isClickable,
  placement,
  trigger,
}: Props) => {
  const refTrigger = useRef<HTMLAnchorElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [isPinned, setIsPinned] = useState(false)

  const setIsOpen = useCallback((isOpen: boolean) => {
    setIsHovering(isOpen)
    setIsPinned(isOpen)
  }, [])

  const onClickTrigger = useCallback(
    (e: MouseEvent) => {
      if (isClickable) {
        if (!e.defaultPrevented) return
      } else {
        e.preventDefault()
      }

      setIsHovering(false)
      setIsPinned((isPinnedPrevious) => !isPinnedPrevious)
    },
    [isClickable],
  )

  const onEscapeKeyDown = useCallback(() => setIsOpen(false), [setIsOpen])

  const onInteractOutside = useCallback<FloatingInteractOutsideType>(
    (e) => {
      if (refTrigger.current?.contains(e.target as Node)) {
        e.preventDefault()
        return
      }

      if (e.defaultPrevented) return

      setIsOpen(false)
    },
    [setIsOpen],
  )

  // Clear the latched state so it cannot reopen once forceClosed lifts
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (forceClosed) setIsOpen(false)
  }, [forceClosed, setIsOpen])

  const isOpen = (isHovering || isPinned) && !forceClosed

  return (
    <Root closeDelay={CLOSE_DELAY} onOpenChange={setIsHovering} open={isOpen} openDelay={OPEN_DELAY}>
      <Trigger asChild onClick={onClickTrigger} ref={refTrigger}>
        {trigger}
      </Trigger>
      <Portal>
        <Content
          {...getFloatingContentProps({ className, ignoreScopes, placement })}
          onEscapeKeyDown={onEscapeKeyDown}
          onInteractOutside={onInteractOutside}
        >
          <FloatingProvider setIsOpen={setIsOpen}>{children}</FloatingProvider>
        </Content>
      </Portal>
    </Root>
  )
}
